import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRef, useImperativeHandle, forwardRef } from 'react';
import Paper from '@mui/material/Paper';
import { Box, Typography, Card, CardContent, Stack, Divider, Chip } from '@mui/material';
import { InputLabel, FormControl, Select, Menu, MenuItem, ListSubheader, IconButton, Tooltip } from '@mui/material';
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined';
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AddModal from './AddModal';
import { httpRequest, httpRequestUtil } from '../api/helper';
import { useNotification } from '../context/NotificationContext';
import { formatTimeToAmPm } from '../common/AppUtils';
import { API_ERROR_MESSAGE, TT_POLL_QUICK_CUTOFF, TT_POLL_INTERVAL_FINAL, TT_POLL_INTERVAL_INITIAL, NA_LABEL, TT_BG_COLOR } from '../common/AppConstants';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import TileModal from './TileModal';

const NO_DATA_AVAILABLE_STRING = 'Please create a new schedule or update the filters !!!';
const CELL_TYPE = 'CourseClass'; // Type for draggable items
const TABLE_CONTAINER = 'table';
const UNASSIGNED_CONTAINER = 'unassigned';
const CELL_HEIGHT = 68;
const CELL_SPACING_RIGHT = 2; // If multiple tiles in same cell, 4
const CELL_PADDING_RIGHT = 4;  // Final padding will add CELL_SPACING_RIGHT as well, 8
const CELL_PADDING_BOTTOM = 2;  // 4
const GRID_BORDER = '1px solid #ddd';
const TILE_BORDER_RADIUS = '6px';
const TIME_SLOT_COLOR = '#f0f0f0';
const SCHEDULE_API_URL = 'time-table/time-table/';

const SUBJECT_SESSION_API = 'time-table/subject-session/';

const VIEW_TYPE_LIST = [
  {
    attr: 'section',
    label: 'Section',
    data_list: [
      'staff',
      'classroom',
    ],
  },
  {
    attr: 'staff',
    label: 'Teacher',
    data_list: [
      'section',
      'classroom',
    ],
  },
  {
    attr: 'classroom',
    label: 'Classroom',
    data_list: [
      'section',
      'staff',
    ],
  },
  {
    attr: 'day',
    label: 'Master',
    data_list: [
      'staff',
      'classroom',
    ],
  },
];

const getDefaultView = (props) => {
  if (props.masterView) return 'day'; // Schedule.js
  if (props.stage==='view') return 'staff'; // Schedule.js
  return 'section'; // CreateSchedule.js
};

const column_map = {
  section: {
    attr: 'section',
    editable: false,
    extra: false,
    label: 'Section',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Student Group',
  },
  subject: {
    attr: 'subject',
    editable: true,
    extra: false,
    label: 'Subject',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Subject',
  },
  slot_size: {
    attr: 'slot_size',
    editable: true,
    extra: false,
    label: 'Slot Size',
    related: false,
    required: true,
    type: 'text',
    help_text: 'Slot Size',
  },
  staff: {
    attr: 'staff',
    editable: true,
    extra: false,
    label: 'Teacher',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Teacher',
  },
  classroom: {
    attr: 'classroom',
    editable: true,
    extra: false,
    label: 'Classroom',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Classroom',
  }
}

const TileModalColumns = [
  column_map.section,
  column_map.subject,
  column_map.slot_size,
  column_map.staff,
  column_map.classroom,
];

const subjectAddColumns = [
  column_map.subject,
  column_map.slot_size,
  column_map.staff,
  column_map.classroom,
];

const dropdownHeaderSx = {
  fontWeight: 600,
  fontSize: 13,
  lineHeight: 1.8,
};

const dropdownItemSx = {
  pl: 3,
};

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const getCellColor = (cellDict) => {
  if (cellDict.is_reserved)
    return TT_BG_COLOR.RESERVED
  if (cellDict.is_break)
    return TT_BG_COLOR.BREAK
  return TT_BG_COLOR.CELL
};

const TimeTable = forwardRef((props, ref) => {
  console.log("### RENDERING TimeTable ###");
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(true);

  const [viewType, setViewType] = useState(getDefaultView(props));
  const [viewElement, setViewElement] = useState(null); // particular element of viewType
  const [conflictView, setConflictView] = useState(""); // particular element of viewType

  const [canBeSaved, setCanBeSaved] = useState(false);
  const [anchorActionEl, setAnchorActionEl] = useState(null);
  const actionElOpen = Boolean(anchorActionEl);
  const [isSubjectAddOpen, setSubjectAddOpen] = useState(false);


  const [template, setTemplate] = useState({
    label: '',
    period_label_list: [],
    day_list: [],
  });
  const [scheduleData, setScheduleData] = useState({});
  const [scheduleRelatedData, setScheduleRelatedData] = useState({});
  const [scheduleStatus, setScheduleStatus] = useState(null);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1000);

  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  useImperativeHandle(ref, () => ({
    onValidate: async (tt_stage) => {
      if (tt_stage === 'manual') {
        return await handleScheduleAutoGenerate(tt_stage);
      } else {
        return await handleScheduleUpdateVerifyStage(tt_stage);
      }
    },
  }));

  useEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.offsetWidth);
        }
      });
      resizeObserver.observe(containerRef.current);

      return () => resizeObserver.disconnect();
    }
  }, []);

  const [selectedTile, setSelectedTile] = useState(null);
  const [isTileModalOpen, setIsTileModalOpen] = useState(false);

  const handleTileClick = (tileData) => {
    const cellData = scheduleData['raw'][tileData.id];
    console.log(cellData);
    setSelectedTile(cellData);
  };

  useEffect(() => {
      if (selectedTile !== null && selectedTile !== undefined) {
        // Only open the modal after the tile has been selected
        setIsTileModalOpen(true);
      }
    }, [selectedTile]); // Runs when selectedTile changes

  const handleTileClose = () => {
    setIsTileModalOpen(false);
    setSelectedTile(null);
  };

  const handleTileSave = (updatedData, onFailure) => {
    const newData = { ...scheduleData };
    console.log(updatedData);
    // console.log(newData['raw'][selectedTile.id]);
  
    TileModalColumns.forEach((field) => {
      if (
        field.editable &&
        newData['raw'][selectedTile.id][field.attr] !== updatedData[field.attr]
      ) {
        newData['raw'][selectedTile.id][field.attr] = updatedData[field.attr];
        // console.log(newData['raw'][selectedTile.id][field.attr].label);
      }
    });
    // console.log(newData['raw'][selectedTile.id]);
    handleTileClose();
    setScheduleData(newData);
  };

  const handleActionOpen = (event) => {
    setAnchorActionEl(event.currentTarget);
  };

  const handleActionClose = () => {
    setAnchorActionEl(null);
  };

  const handleAddSession = () => {
    handleActionClose();
    setSubjectAddOpen(true);
  };

  const handleDeleteSession = async (rowData) => {
    handleActionClose();

    try {
      const apiResponse = await httpRequestUtil(
        'DELETE',
        SUBJECT_SESSION_API,
        rowData,
      );

      if (apiResponse.status === 200) {
        addNotification("Deleted successfully.", 'success');
        await fetchScheduleJson();
      } else {
        addNotification("Failed to delete.", 'warning');
      }
    } catch (error) {
      addNotification("Failed to delete", 'warning');
    }
  };

  const handleSubjectAddClose = () => {
    setSubjectAddOpen(false);
  };

  const handleSubjectAddSave = async (addFormData, onAddSuccess, onAddFailure) => {
    const ref_day_period = Object.entries(scheduleRelatedData["day_period"]).find(
      ([_, day_period_dict]) => day_period_dict.is_break === false
    )?.[0];
    const finalFormData = {
      ...addFormData,
      section: viewElement,
      time_table: props.scheduleId,
      day_period: ref_day_period,
    };
    let addResponse = {};
    try {
      addResponse = await httpRequestUtil(
        'POST',
        SUBJECT_SESSION_API,
        finalFormData,
      );
      console.log(addResponse.data);

      if (addResponse.status === 201) {
        addNotification("Added successfully", 'success');
        await fetchScheduleJson();
        onAddSuccess();
      } else {
        addNotification("Failed to add", 'warning');
        onAddFailure(addResponse.data);
      }
    } catch (error) {
      addNotification("Failed to add", 'warning');
      onAddFailure(error?.data);
    }
  };
  
  const handleScheduleAutoGenerate = async (tt_stage) => {
    const payload = {
      tt: props.scheduleId,
      stage: tt_stage,
      raw: scheduleData['raw'],
    };

    const response = await httpRequest({
      method: 'POST',
      url: SCHEDULE_API_URL,
      data: payload,
      addNotification,
    });

    return response.success;
  };

  const handleScheduleUpdateVerifyStage = async (tt_stage) => {
    console.log(tt_stage);
    const payload = {
      tt: props.scheduleId,
      stage: tt_stage,
      raw: scheduleData['raw'],
    };
    try {
      const response = await httpRequestUtil(
        'POST',
        SCHEDULE_API_URL,
        payload,
      );
      if (response.status === 200) {
        addNotification("Changes applied successfully", 'info');
        await fetchScheduleJson();
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.log(API_ERROR_MESSAGE.networkError);
      return false;
    }
  };

  const dynamicCellWidth = Math.max(
    100,
    Math.floor(containerWidth / (template.day_list.length + 1)),
  );

  const dayPeriodLookup = React.useMemo(() => {
    const lookup = {};
    for (const [dayPeriodId, dayPeriod] of Object.entries(scheduleRelatedData?.day_period || {})) {
      const dayIndex = scheduleRelatedData.day[dayPeriod.day].day_of_week;
      const periodIndex = scheduleRelatedData.period[dayPeriod.period].order;

      lookup[`${dayIndex}_${periodIndex}`] = dayPeriodId;
    }
    return lookup;
  }, [scheduleRelatedData]);

  const getDayPeriodId = React.useCallback(
    (dayIndex, periodIndex) => dayPeriodLookup[`${dayIndex}_${periodIndex}`] || null,
    [dayPeriodLookup]
  );

  const moveCell = (source, target) => {
    // Unassigned Area to Unassigned Area
    if (source.type === UNASSIGNED_CONTAINER && target.type === UNASSIGNED_CONTAINER) {
      return;
    }

    const newData = { ...scheduleData };
    if (source.type === UNASSIGNED_CONTAINER) { // Unassigned Area to Table
      newData[viewType][viewElement][target.colIndex][target.rowIndex].tenant_list.push({ ...source.cell });
      newData['unassigned'][viewType][viewElement].splice(source.index, 1);
      newData['raw'][source.cell.id]['day_period'] = getDayPeriodId(target.colIndex, target.rowIndex);
    } else if (target.type === UNASSIGNED_CONTAINER) {  // Table to Unassigned Area
      newData['unassigned'][viewType][viewElement].push({ ...source.cell });
      newData[viewType][viewElement][source.colIndex][source.rowIndex].tenant_list =
        newData[viewType][viewElement][source.colIndex][source.rowIndex].tenant_list.filter((_, index) => index !== source.index);
      newData['raw'][source.cell.id]['day_period'] = null;
    } else {  // Table to Table
      // Remove the cell from the source list
      const sourceList = newData[viewType][viewElement][source.colIndex][source.rowIndex].tenant_list;
      const [movedCell] = sourceList.splice(source.index, 1);

      const targetList = newData[viewType][viewElement][target.colIndex][target.rowIndex].tenant_list;
      targetList.push(movedCell);

      // Update the moved cell's Time Slot
      newData['raw'][movedCell.id]['day_period'] = getDayPeriodId(target.colIndex, target.rowIndex);;
    }

    setScheduleData(newData);
  };

  function TableCell({ rowIndex, colIndex, tenancy, children }) {
    const [, drop] = useDrop({
      accept: CELL_TYPE,
      canDrop: () => colIndex !== -1, // -1 is time label column
      drop: (draggedItem) => {
        const cellData = scheduleData['raw'][draggedItem.cell.id];
        const validTimeSlot = rowIndex + cellData.slot_size <= template.period_label_list.length;
        if (!validTimeSlot) {
          console.log('Session duration is going outside the defined time');
          // addNotification('Session duration is going outside the defined time', 'warning');
        }
        else {
          moveCell(draggedItem, { type: TABLE_CONTAINER, rowIndex, colIndex });
        }
      },
    });

    function sameElectiveGroup(cell_id_list) {
      const elective_group_list = cell_id_list.map(
        cell_pt => scheduleData['raw'][cell_pt.id]?.elective_group ?? null
      );

      // if any null -> automatically false
      if (elective_group_list.some(elective_group => elective_group === null || elective_group === undefined)) {
        return false;
      }

      // check if all equal
      return elective_group_list.every(elective_group => elective_group === elective_group_list[0]);
    }


    const bgColor = colIndex < 0 ? TT_BG_COLOR.HEADER : (
      tenancy.length > 1 && ( viewType !== 'section' || !sameElectiveGroup(tenancy) ) ? TT_BG_COLOR.CONFLICT :
      template.day_list[colIndex].period_list[rowIndex].is_reserved ? TT_BG_COLOR.RESERVED :
      template.day_list[colIndex].period_list[rowIndex].is_break ? TT_BG_COLOR.BREAK :
      TT_BG_COLOR.CELL
    );

    return (
      <Box
        ref={drop}
        sx={{
          height: `${CELL_HEIGHT}px`,
          width: '100%',
          // width: `${dynamicCellWidth}px`,
          borderRight: GRID_BORDER,
          borderBottom: GRID_BORDER,
          borderLeft: colIndex === -1 ? GRID_BORDER : 'none',
          // borderTop: colIdx === -1 ? TIME_SLOT_COLOR : "none",
          borderBottomLeftRadius:
            rowIndex === template.period_label_list.length - 1 && colIndex === -1
              ? TILE_BORDER_RADIUS
              : 0,
          borderBottomRightRadius:
            rowIndex === template.period_label_list.length - 1 &&
            colIndex === template.day_list.length - 1
              ? TILE_BORDER_RADIUS
              : 0,
          backgroundColor:
            bgColor,
        }}
      >
        {children}
      </Box>
    );
  }

  function TimeLabel({ top, left, width, rowIndex, cell }) {
    const cellData = cell;

    return (
      <Paper
        sx={{
          position: 'absolute',
          top: top,
          left: left + 1,
          height: CELL_HEIGHT - 1,
          width: width - 2,
          backgroundColor: TIME_SLOT_COLOR,
          borderRadius: 0,
          boxShadow: 0,
        }}
      >
        {/* Content */}
        <Box
          sx={{
            py: 0.25, px: 0.5,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="body1"
            fontWeight="400"
            textAlign="center"
            noWrap
            sx={{ fontSize: '0.8rem', mb: 0.25 }}
          >
            {cellData.name}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 0.25, alignItems: 'flex-start', pl: 0.25 }}>
            {cellData.start_time && cellData.end_time ? (
              <>
                <Chip
                  label={formatTimeToAmPm(cellData.start_time)}
                  // onClick={onClick}
                  size="small"
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    height: 16,
                    minWidth: 32,
                    fontSize: '0.6rem',
                  }}
                  aria-label={`Start time: ${formatTimeToAmPm(cellData.start_time)}`}
                />
                <Chip
                  label={formatTimeToAmPm(cellData.end_time)}
                  // onClick={onClick}
                  size="small"
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    height: 16,
                    minWidth: 32,
                    fontSize: '0.6rem',
                  }}
                  aria-label={`End time: ${formatTimeToAmPm(cellData.end_time)}`}
                />
              </>
            ) : (
              <Chip
                label="Select Time"
                // onClick={onClick}
                size="small"
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  backgroundColor: 'grey.200',
                  color: 'text.primary',
                  height: 16,
                  minWidth: 32,
                  fontSize: '0.6rem',
                }}
                aria-label="Select Time"
              />
            )}
          </Box>
        </Box>
      </Paper>
    );
  }

  function TableTile({ top, left, width, rowIndex, colIndex, index, cell, onClick }) {
    const cellData = scheduleData['raw'][cell.id];
    const height = cellData.slot_size * CELL_HEIGHT;
    const finalHeight = height - CELL_PADDING_BOTTOM;

    const [{ isDragging }, drag] = useDrag({
      type: CELL_TYPE,
      item: { type: TABLE_CONTAINER, rowIndex, colIndex, index, cell },
      canDrag: props.isEditable,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const electiveName = cellData.elective_group ? "Elective" : null;

    return (
      <Paper
        ref={drag}
        sx={{
          position: 'absolute',
          top: top,
          left: left,
          height: finalHeight,
          boxShadow: 0,
          width: width - CELL_SPACING_RIGHT,
          borderRadius: TILE_BORDER_RADIUS,
          backgroundColor: scheduleRelatedData.subject[cellData.subject].color || '#fff',
          cursor: 'grab',
          opacity: isDragging ? 0.4 : 1,
          flexDirection: 'row',
          zIndex: 10 + index,
          display: 'flex', // flex layout for the bar and content
          // Hover effects
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          '&:active': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }
        }}
        onClick={onClick}
      >
        {/* Colored bar */}
        {/* <Box
          sx={{
            width: COLOURED_BAR_WIDTH,
            height: '100%',
            backgroundColor: verticalBarColor,
            flexShrink: 0,
            borderTopLeftRadius: TILE_BORDER_RADIUS,
            borderBottomLeftRadius: TILE_BORDER_RADIUS,
          }}
        /> */}

        {/* Tile Text Content */}
        <Box
          sx={{ flexGrow: 1, py: 0.25, px: 0.5, alignContent: 'top', overflow: 'hidden' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              variant="body1"
              fontWeight="400"
              textAlign="left"
              noWrap
              sx={{ fontSize: '0.8rem', mb: 0.25 }}
            >
              {scheduleRelatedData.subject[cellData.subject].name || cellData.label}
            </Typography>
            { electiveName && (
              <Tooltip title={electiveName} arrow>
                <IconButton onClick={() => null} size="small" sx={{ p: 0}}>
                  <HowToVoteOutlinedIcon color='primary' sx={{ fontSize: '1.0rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 0.25, alignItems: 'flex-start', pl: 0.25 }}>
            {
              VIEW_TYPE_LIST
              .filter((view_dict) => view_dict.attr === viewType)
              .map((view_dict) =>
                view_dict.data_list.map((attr) => (
                  <Chip
                    key={cellData[attr]}
                    label={scheduleRelatedData[attr][cellData[attr]]?.label}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 16,
                      minWidth: 32,
                      fontSize: '0.6rem',
                      borderColor: '#cccccc',
                      borderRadius: 1, // This makes it rectangular with rounded corners
                      '& .MuiChip-label': { px: 0.5 }
                    }}
                  />
                ))
              )
            }
          </Box>
        </Box>
      </Paper>
    );
  }

  function UnassignedArea({ width }) {
    const [, drop] = useDrop({
      accept: CELL_TYPE,
      drop: (draggedItem) => {
        moveCell(draggedItem, { type: UNASSIGNED_CONTAINER });
      },
    });

    const unassignedCellWidth = Math.min(width, 150);

    return (
      <Box>
        <Card
          ref={drop}
          sx={{
            // width: 200,
            minHeight: 100,
            border: '1px dashed gray',
            backgroundColor: '#f9f9f9',
            mt: 2,
          }}
        >
          <CardContent
            sx={{
              pt: 0,
            }}
          >
            <Box
              key="unassigned-header"
              sx={{
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body1" textAlign="center">
                Unassigned
              </Typography>
            </Box>
            <Divider />
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              mt={2}
              justifyContent="left"
            >
              {scheduleData['unassigned'][viewType][viewElement].map(
                (cell, index) => {
                  return (
                    <UnassignedTile
                      key={index}
                      cell={cell}
                      index={index}
                      width={unassignedCellWidth}
                      onClick={() => handleTileClick(cell)}
                    />
                  );
                },
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  function UnassignedTile({ cell, index, width, onClick }) {
    const cellData = scheduleData['raw'][cell.id];

    const [{ isDragging }, drag] = useDrag({
      type: CELL_TYPE,
      item: { type: UNASSIGNED_CONTAINER, index, cell },
      canDrag: props.isEditable,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const electiveName = cellData.elective_group ? "Elective" : null;

    return (
      <Box
        ref={drag}
        sx={{
          height: CELL_HEIGHT, // CELL_HEIGHT - CELL_PADDING_BOTTOM
          width: width,
          border: GRID_BORDER,
          borderRadius: TILE_BORDER_RADIUS,
          backgroundColor: scheduleRelatedData.subject[cellData.subject].color || '#fff',
          cursor: 'grab',
          opacity: isDragging ? 0.5 : 1,
          flexDirection: 'row',
          display: 'flex', // flex layout for the bar and content
          // Hover effects
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          '&:active': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }
        }}
        onClick={onClick}
      >
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Tile Text Content */}
          <Box
            sx={{ flexGrow: 1, py: 0.25, px: 0.5, alignContent: 'top', overflow: 'hidden' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography
                variant="body1"
                fontWeight="400"
                textAlign="left"
                noWrap
                sx={{ fontSize: '0.8rem', mb: 0.25 }}
              >
                {scheduleRelatedData.subject[cellData.subject].name || cellData.label}
              </Typography>
              { electiveName && (
                <Tooltip title={electiveName} arrow>
                  <IconButton onClick={() => null} size="small" sx={{ p: 0}}>
                    <HowToVoteOutlinedIcon color='primary' sx={{ fontSize: '1.0rem' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 0.25, alignItems: 'flex-start', pl: 0.25 }}>
              {
                VIEW_TYPE_LIST
                .filter((view_dict) => view_dict.attr === viewType)
                .map((view_dict) =>
                  view_dict.data_list.map((attr) => (
                    <Chip
                      key={cellData[attr]}
                      label={scheduleRelatedData[attr][cellData[attr]]?.label}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 16,
                        minWidth: 32,
                        fontSize: '0.6rem',
                        borderColor: '#cccccc',
                        borderRadius: 1, // This makes it rectangular with rounded corners
                        '& .MuiChip-label': { px: 0.5 }
                      }}
                    />
                  ))
                )
              }
            </Box>
          </Box>

          {/* Duration dots */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row-reverse', // start from right
              gap: '2px',
              px: 1,
              pb: 0.5,
            }}
          >
            {[...Array(cellData.slot_size)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '40%',
                  backgroundColor: 'rgba(0,0,0,0.5)', // or use a color from cellData
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  const handleViewTypeChange = (value) => {
    setViewElement(null);
    setViewType(value);
  };

  useEffect(() => {
    if (scheduleData && scheduleData[viewType]) {
      const c_view_parts = conflictView.split(":");
      if (c_view_parts.length === 2 && viewElement !== c_view_parts[1]) {
        setViewElement(c_view_parts[1]);
      } else {
        setViewElement(Object.keys(scheduleData[viewType])[0]);
      }
    }
  }, [viewType]);

  useEffect(() => {
    const c_view_parts = conflictView.split(":");
    if (c_view_parts.length === 2) {
      setViewElement(c_view_parts[1]);
      setViewType(c_view_parts[0]);
    }
  }, [conflictView]);

  const handleViewElementChange = (value) => {
    setViewElement(value);
  };

  const handleConflictViewChange = (value) => {
    setConflictView(value);
  };

  const fetchScheduleJson = async (polling = false) => {
    if (!polling) {
      setIsLoading(true);
    }

    const payload = {
      tt: props.scheduleId,
      master_view: props.masterView,
    }

    await httpRequest({
      method: 'GET',
      url: SCHEDULE_API_URL,
      data: payload,
      onSuccess: (data) => {
        // setViewType(Object.keys(data.rows)[0]);
        // setViewElement(Object.keys(data.rows[Object.keys(data.rows)[0]])[0]);
        if (viewElement === null) {
          setViewElement(Object.keys(data.rows[viewType])[0]);
        }
        if (!polling) {
          setTemplate(data.canvas.template);
        }
        setScheduleData(data.rows);
        setScheduleStatus(data.status);
        if (props.stage !== 'view') {
          props.canBeSaved(data.canBeSaved);
          setCanBeSaved(data.canBeSaved);
        }
        // setUnassignedList(data.rows["unassigned"]?.[viewType]?.[Object.keys(data.rows[viewType])[0]] || []);
        setScheduleRelatedData(data.relatedData);
      },
      onFailure: (data) => {
        // setViewType(null);
        setViewElement('');
        setTemplate({ label: '', period_label_list: [], day_list: [] });
        setScheduleData({});
        setScheduleStatus(null);
        // setUnassignedList([]);
        setScheduleRelatedData({});
      },
      addNotification,
    });

    if (!polling) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (props.scheduleId) {
      // Check not null
      fetchScheduleJson();
    }
  }, [props.scheduleId]);

  useEffect(() => {
    if (scheduleStatus !== 'in_progress') {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      startTimeRef.current = null;
      return;
    }

    if (intervalRef.current) return;

    startTimeRef.current = Date.now();
    // fetchScheduleJson(true);

    const poll = () => {
      const elapsed = Date.now() - (startTimeRef.current ?? Date.now());
      const nextInterval = elapsed >= TT_POLL_QUICK_CUTOFF ? TT_POLL_INTERVAL_FINAL : TT_POLL_INTERVAL_INITIAL;

      intervalRef.current = setTimeout(() => {
        fetchScheduleJson(true);
        poll();
      }, nextInterval);
    };

    poll();

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      startTimeRef.current = null;
    };
  }, [scheduleStatus]); // [scheduleStatus, fetchScheduleJson]

  const total_conflict_views = Object.keys(scheduleData?.conflict?.section || {}).length +
    Object.keys(scheduleData?.conflict?.staff || {}).length + Object.keys(scheduleData?.conflict?.classroom || {}).length;

  return (
    <Box>
      {scheduleData.length === 0 ? (
        // Display message when there are no rows
        <Box
          sx={{
            textAlign: 'left',
          }}
        >
          <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
        </Box>
      ) : (
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Filters and controls */}
          {/* ToDo: Add inline filters in table itself */}
          {!isLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 1,
                mb: 2,
                gap: 2,
              }}
            >
              <FormControl sx={{ flex: 1 }}>
                <InputLabel id="view_type">View Type</InputLabel>
                <Select
                  labelId="view_type"
                  id="view_type"
                  value={viewType}
                  label="View Type"
                  onChange={(e) => handleViewTypeChange(e.target.value)}
                >
                  {
                    VIEW_TYPE_LIST
                    .filter(view_dict =>
                      props.masterView === true
                        ? view_dict.attr === "day"
                        : view_dict.attr !== "day"
                    )
                    .map(view_dict => (
                      <MenuItem key={view_dict.attr} value={view_dict.attr}>
                        {view_dict.label}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <InputLabel id="view_element">{capitalize(viewType)}</InputLabel>
                <Select
                  labelId="view_element"
                  id="view_element"
                  value={viewElement}
                  label={capitalize(viewType)}
                  onChange={(e) => handleViewElementChange(e.target.value)}
                >
                  {viewType && scheduleData[viewType] ? (
                    Object.keys(scheduleData[viewType]).map((option_item) => (
                      <MenuItem key={option_item} value={option_item}>
                        {scheduleRelatedData[viewType]?.[option_item]?.label}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No options available</MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Conflicts Dropdown */}
              {total_conflict_views > 0 && (
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel id="conflict_view">Conflicts = {total_conflict_views}</InputLabel>
                  <Select
                    labelId="conflict_view"
                    id="conflict_view"
                    value={conflictView}
                    label={`Conflicts = ${total_conflict_views}`}
                    onChange={(e) => handleConflictViewChange(e.target.value)}
                  >
                    {/* <MenuItem value="">
                      <em>Select</em>
                    </MenuItem> */}

                    {/* SECTION GROUP */}
                    {Object.keys(scheduleData.conflict.section).length > 0 &&
                      <ListSubheader sx={dropdownHeaderSx}>Section</ListSubheader>
                    }
                    {Object.keys(scheduleData.conflict.section).length > 0 &&
                      Object.entries(scheduleData.conflict.section).map(([id, count]) => (
                        <MenuItem key={id} value={`section:${id}`} sx={dropdownItemSx}>
                          {scheduleRelatedData.section?.[id]?.label ?? id} ({count})
                        </MenuItem>
                      )
                    )}

                    {/* STAFF GROUP */}
                    {Object.keys(scheduleData.conflict.staff).length > 0 &&
                      <ListSubheader sx={dropdownHeaderSx}>Staff</ListSubheader>
                    }
                    {Object.keys(scheduleData.conflict.staff).length > 0 &&
                      Object.entries(scheduleData.conflict.staff).map(([id, count]) => (
                        <MenuItem key={id} value={`staff:${id}`} sx={dropdownItemSx}>
                          {scheduleRelatedData.staff?.[id]?.label ?? id} ({count})
                        </MenuItem>
                      )
                    )}

                    {/* CLASSROOM GROUP */}
                    {Object.keys(scheduleData.conflict.classroom).length > 0 &&
                      <ListSubheader sx={dropdownHeaderSx}>Classroom</ListSubheader>
                    }
                    {Object.keys(scheduleData.conflict.classroom).length > 0 &&
                      Object.entries(scheduleData.conflict.classroom).map(([id, count]) => (
                        <MenuItem key={id} value={`classroom:${id}`} sx={dropdownItemSx}>
                          {scheduleRelatedData.classroom?.[id]?.label ?? id} ({count})
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              )}

              {/* Action Button */}
              {props.isEditable && canBeSaved && viewType === 'section' && (
                <>
                  <IconButton
                    onClick={handleActionOpen}
                    sx={{
                      backgroundColor: "action.hover",
                      "&:hover": {
                        backgroundColor: "primary.light",
                      },
                    }}
                  >
                    <MoreHorizIcon/>
                  </IconButton>
                  <Menu
                    anchorEl={anchorActionEl}
                    open={actionElOpen}
                    onClose={handleActionClose}
                  >
                    <MenuItem onClick={handleAddSession}>
                      Add new Session
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          )}

          {/* Table */}
          {!isLoading && viewElement != null && scheduleData[viewType][viewElement] && (
            <DndProvider backend={HTML5Backend}>
              <Box
                display="flex"
                flexDirection="column"
                sx={{ overflowX: 'auto' }}
              >
                {/* Table */}
                <Box flexGrow={1}>
                  {/* Column Headers */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${template.day_list.length + 1}, ${dynamicCellWidth}px)`,
                      // mb: 1,
                    }}
                  >
                    {/* Top-left label */}
                    <Box
                      key="table-head-label"
                      sx={{
                        width: `${dynamicCellWidth}px`,
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRight: GRID_BORDER,
                        borderBottom: GRID_BORDER,
                        borderLeft: GRID_BORDER,
                        borderTopLeftRadius: TILE_BORDER_RADIUS,
                        borderTopRightRadius: 0,
                        borderTop: GRID_BORDER,
                        backgroundColor: '#f0f0f0',
                      }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight="400"
                        textAlign="center"
                        // sx={{ fontSize: '0.8rem' }}
                      >
                        {template.name}
                      </Typography>
                    </Box>

                    {/* Day Headers */}
                    {template.day_list.map((day_dict, idx) => (
                      <Box
                        key={`day-header-${idx}`}
                        sx={{
                          width: `${dynamicCellWidth}px`,
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRight: GRID_BORDER,
                          borderBottom: GRID_BORDER,
                          borderLeft: 'none',
                          borderTopLeftRadius: 0,
                          borderTopRightRadius:
                            idx === template.day_list.length - 1
                              ? TILE_BORDER_RADIUS
                              : 0,
                          borderTop: GRID_BORDER,
                          backgroundColor: '#f0f0f0',
                        }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight="400"
                          textAlign="center"
                          // sx={{ fontSize: '0.8rem' }}
                        >
                          {day_dict.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Grid Scaffold */}
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    {/* Grid background */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${template.day_list.length + 1}, ${dynamicCellWidth}px)`,
                        gridTemplateRows: `repeat(${template.period_label_list.length}, ${CELL_HEIGHT}px)`,
                      }}
                    >
                      {template.period_label_list.map((time_label, rowIdx) => (
                        <React.Fragment key={`row-${rowIdx}`}>
                          <TableCell
                            key={`time-label-${rowIdx}`}
                            rowIndex={rowIdx}
                            colIndex={-1}
                            tenancy={[]}
                          >
                            <TimeLabel
                              key={`time-${rowIdx}`}
                              top={rowIdx * CELL_HEIGHT}
                              left={0}
                              width={dynamicCellWidth}
                              rowIndex={rowIdx}
                              cell={time_label}
                            />
                          </TableCell>
                          {/* Schedule data cells for each day */}
                          {scheduleData[viewType][viewElement].map((day, colIdx) => {
                            const cellItems = day[rowIdx].tenant_list;
                            return (
                              <TableCell
                                key={`tabel-cell-${rowIdx}-${colIdx}`}
                                rowIndex={rowIdx}
                                colIndex={colIdx}
                                tenancy={day[rowIdx].tenancy}
                              >
                                {cellItems.map((cc_class, classIndex) => {
                                  const itemWidth = ( dynamicCellWidth - CELL_PADDING_RIGHT ) / cellItems.length;
                                  return (
                                    <TableTile
                                      key={`tabel-tile-${rowIdx}-${colIdx}-${classIndex}`}
                                      top={rowIdx * CELL_HEIGHT}
                                      left={(colIdx + 1) * dynamicCellWidth + classIndex * itemWidth}
                                      width={itemWidth}
                                      rowIndex={rowIdx}
                                      colIndex={colIdx}
                                      index={classIndex}
                                      cell={cc_class}
                                      onClick={() => handleTileClick(cc_class)}
                                    />
                                  );
                                })}
                              </TableCell>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* Unassigned Area */}
                {props.isEditable && <UnassignedArea width={dynamicCellWidth} />}
              </Box>
            </DndProvider>
          )}
        </Box>
      )}
      {/* Tile Modal */}
      <Box>
        <TileModal
          open={isTileModalOpen}
          columns={TileModalColumns}
          rowData={selectedTile}
          relatedData={scheduleRelatedData}
          handleClose={handleTileClose}
          handleSave={handleTileSave}
          handleDelete={handleDeleteSession}
          isEditable={props.isEditable}
        />
      </Box>

      {/* Assign Subject Modal */}
      <Box>
        <AddModal
          open={isSubjectAddOpen}
          columns={subjectAddColumns}
          relatedData={scheduleRelatedData}
          rows={scheduleData['raw']}
          handleClose={handleSubjectAddClose}
          handleSave={handleSubjectAddSave}
          add_title={`${scheduleRelatedData?.[viewType]?.[viewElement]?.name} : Assign Subject`}
        />
      </Box>
    </Box>
  );
});

export default TimeTable;
